import {FC} from 'react'

import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Button} from '../content/Button'
import {ButtonVariant} from '../content/ButtonTypes'
import {Text, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'
import {HowItWorksCarousel} from './carousel/HowItWorksCarousel'
import {HowItWorksStep} from './step/HowItWorksStep'

export type HowItWorksStepType = {
  title: string
  duration?: string
  description: string
  imageSrc?: string
  imageAlt?: string
  iconSrc?: string
  iconAlt?: string
  linkHref?: string
  linkText?: string
}

type HowItWorksProps = {
  title: string
  titleVariant?: TextVariant
  description?: string
  steps: HowItWorksStepType[]
  buttonText?: string
  buttonLink?: string
  buttonVariant?: ButtonVariant
  onButtonClick?: () => void
  maxWidth?: string
  stepMaxWidth?: string
  isCentered?: boolean
}

export const HowItWorks: FC<React.PropsWithChildren<HowItWorksProps>> = ({
  title,
  titleVariant = TextVariant.h2,
  description,
  steps,
  buttonText,
  buttonLink,
  buttonVariant,
  onButtonClick,
  maxWidth = '600px',
  stepMaxWidth,
  isCentered = false,
}) => {
  const {t} = useTranslation()
  const isPrimaryButtonVariant = buttonVariant === ButtonVariant.primary

  return (
    <>
      <div className="wrapper">
        <Text variant={titleVariant} align="center">
          {title}
        </Text>
        {description && (
          <div className="description">
            <Text variant={TextVariant.text20} color={theme.palette.text.light} align="center">
              {description}
            </Text>
          </div>
        )}
      </div>
      <ul className="desktop-wrapper">
        {steps.map(({title, duration, description, iconSrc, iconAlt}, i) => (
          <li key={title} className="item">
            <HowItWorksStep
              title={t(title)}
              duration={t(duration)}
              description={t(description)}
              imageSrc={iconSrc}
              imageAlt={iconAlt}
              index={i + 1}
              isCentered={isCentered}
            />
          </li>
        ))}
      </ul>
      <div className="mobile-wrapper">
        <HowItWorksCarousel>
          {steps.map(({title, duration, description, iconSrc, iconAlt}, i) => (
            <HowItWorksStep
              key={title}
              title={t(title)}
              duration={t(duration)}
              description={t(description)}
              imageSrc={iconSrc}
              imageAlt={iconAlt}
              index={i + 1}
            />
          ))}
        </HowItWorksCarousel>
      </div>
      {buttonText && buttonLink && (
        <div className="button-wrapper">
          <Link href={buttonLink} onClick={onButtonClick}>
            <Button
              type="button"
              icon={
                <ArrowIcon
                  color={
                    isPrimaryButtonVariant ? theme.palette.text.white : theme.palette.primary.main
                  }
                />
              }
              variant={buttonVariant}
              isHoverable={!isPrimaryButtonVariant}
            >
              {buttonText}
            </Button>
          </Link>
        </div>
      )}

      <style jsx>{`
        .wrapper {
          max-width: ${maxWidth};
          margin: 0 auto;
        }

        .description {
          margin-top: ${theme.spacing[3.5]};
        }

        .desktop-wrapper {
          display: flex;
          justify-content: space-between;
          gap: ${theme.spacing[5.5]};
          margin-top: ${theme.spacing[8]};
        }

        .item {
          flex: 1;
          ${stepMaxWidth ? `max-width: ${stepMaxWidth};` : ''}
        }

        .mobile-wrapper {
          display: none;
        }

        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[6]};
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .desktop-wrapper {
            display: none;
          }

          .mobile-wrapper {
            display: block;
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
